import { IssueTimelineDto } from '@issuemanager/api-client';
import { extractEmailFromUserString } from '@issuemanager/apps/client/utils';
import { useSeverityLocalizer } from '@issuemanager/client/features/issues';
import {
  EditableIssueFields,
  IssueClassificationEnum,
  IssueResolutionEnum,
} from '@issuemanager/constants';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IssueTimelineAttachmentsEvents } from './issue-timeline-attachments-events';
import { TimelineEventTypography } from './timeline-event-typography';

interface IssueTimelineEditedEventsProps {
  events: IssueTimelineDto[];
}

const IMPACT_TRUNCATE_LENGTH = 30;

const truncateText = (text: string | undefined): string => {
  if (!text) return '';
  return text.length > IMPACT_TRUNCATE_LENGTH
    ? `${text.substring(0, IMPACT_TRUNCATE_LENGTH)}...`
    : text;
};

export function IssueTimelineEditedEvents({
  events,
}: IssueTimelineEditedEventsProps): React.ReactElement {
  const { t } = useTranslation('common');
  const { getSeverityLabel } = useSeverityLocalizer();
  const classificationLabelMap: Record<string, string> = {
    [IssueClassificationEnum.DEFICIENCY]: t('issueClassification.deficiency'),
    [IssueClassificationEnum.RECOMMENDATION]: t(
      'issueClassification.recommendation',
    ),
  };

  const resolutionLabelMap: Record<string, string> = {
    [IssueResolutionEnum.NOT_ACCEPTED]: t('issueResolution.notAccepted'),
    [IssueResolutionEnum.RESOLVED]: t('issueResolution.resolved'),
    [IssueResolutionEnum.UNRESOLVED]: t('issueResolution.unresolved'),
  };

  const getLabel = (
    value: string | undefined,
    labelMap: Record<string, string>,
  ): string | undefined => {
    if (!value) return undefined;
    return labelMap[value] ?? value;
  };

  const getTextFieldTimelineKey = (
    baseKey: string,
    hasFrom: boolean,
    hasTo: boolean,
  ): string => {
    if (hasFrom && hasTo) {
      return `issueTimeline.${baseKey}Changed`;
    }
    if (hasTo) {
      return `issueTimeline.${baseKey}Set`;
    }
    return `issueTimeline.${baseKey}Removed`;
  };

  const parseWatcherUsers = (
    value: string | undefined,
  ): { name: string; email: string }[] => {
    if (!value) return [];

    const userNamesAndEmails = value
      .split(', ')
      .map(
        (entry) => extractEmailFromUserString(entry) ?? { name: '', email: '' },
      );

    return userNamesAndEmails;
  };

  const renderRelatedOrgUnits = (
    from: string | undefined,
    to: string | undefined,
  ): React.ReactNode[] => {
    const fromOrgUnits: string[] = from && JSON.parse(from);
    const toOrgUnits: string[] = to && JSON.parse(to);

    const addedOrgUnits = toOrgUnits?.filter(
      (orgUnit) => !fromOrgUnits?.includes(orgUnit),
    );

    const removedOrgUnits = fromOrgUnits?.filter(
      (orgUnit) => !toOrgUnits?.includes(orgUnit),
    );

    const elements: React.ReactNode[] = [];

    addedOrgUnits?.forEach((orgUnit) => {
      elements.push(
        <Stack direction='row' gap={1 / 2}>
          <Trans
            t={t}
            i18nKey='issueTimeline.relatedOrgUnitAdded'
            values={{ to: orgUnit }}
          />
        </Stack>,
      );
    });

    removedOrgUnits?.forEach((orgUnit) => {
      elements.push(
        <Stack direction='row' gap={1 / 2}>
          <Trans
            t={t}
            i18nKey='issueTimeline.relatedOrgUnitRemoved'
            values={{ from: orgUnit }}
          />
        </Stack>,
      );
    });

    return elements;
  };

  return (
    <Box>
      <Typography>{t('issueTimeline.issueEdited')}</Typography>

      <Box pl={5 / 2} pt={1} data-testid='issue-timeline-events'>
        {events.map((event) => {
          const { editedField, editedFrom, editedTo } =
            event.bodyData.parameters;

          const editedFromData = extractEmailFromUserString(editedFrom?.value);
          const editedToData = extractEmailFromUserString(editedTo?.value);

          let dueDateTimelineEventKey = 'issueTimeline.dueDateSet';
          if (editedFrom?.value?.length && editedTo?.value?.length) {
            dueDateTimelineEventKey = 'issueTimeline.dueDateChanged';
          } else if (editedFrom?.value?.length && !editedTo?.value?.length) {
            dueDateTimelineEventKey = 'issueTimeline.dueDateRemoved';
          }

          let impactTimelineEventKey: string | null = null;
          if (editedFrom?.value?.length && editedTo?.value?.length) {
            impactTimelineEventKey = 'issueTimeline.impactChanged';
          } else if (editedTo?.value?.length) {
            impactTimelineEventKey = 'issueTimeline.impactSet';
          } else if (editedFrom?.value?.length) {
            impactTimelineEventKey = 'issueTimeline.impactRemoved';
          }

          return (
            <React.Fragment key={event.id}>
              {editedField?.value === EditableIssueFields.SEVERITY && (
                <TimelineEventTypography data-testid='severity-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={
                      editedFrom?.value?.length
                        ? 'issueTimeline.severityChanged'
                        : 'issueTimeline.severitySet'
                    }
                    values={{
                      from: getSeverityLabel(editedFrom?.value),
                      to: getSeverityLabel(editedTo?.value),
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.DUE_DATE && (
                <TimelineEventTypography data-testid='due-date-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={dueDateTimelineEventKey}
                    values={{
                      from: editedFrom?.value,
                      to: editedTo?.value,
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.ISSUE_OWNER && (
                <Box data-testid='issue-owner-timeline-event'>
                  {editedFromData && editedToData ? (
                    <>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueOwnerChanged'
                          values={{
                            name: editedToData.name,
                            email: editedToData.email,
                          }}
                        />
                      </TimelineEventTypography>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueOwnerRemoved'
                          values={{
                            name: editedFromData.name,
                            email: editedFromData.email,
                          }}
                        />
                      </TimelineEventTypography>
                    </>
                  ) : (
                    <>
                      {editedFromData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueOwnerRemoved'
                            values={{
                              name: editedFromData.name,
                              email: editedFromData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                      {editedToData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueOwnerAdded'
                            values={{
                              name: editedToData.name,
                              email: editedToData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {editedField?.value === EditableIssueFields.ISSUE_APPROVER && (
                <Box data-testid='issue-approver-timeline-event'>
                  {editedFromData && editedToData ? (
                    <>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueApproverChanged'
                          values={{
                            name: editedToData.name,
                            email: editedToData.email,
                          }}
                        />
                      </TimelineEventTypography>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueApproverRemoved'
                          values={{
                            name: editedFromData.name,
                            email: editedFromData.email,
                          }}
                        />
                      </TimelineEventTypography>
                    </>
                  ) : (
                    <>
                      {editedFromData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueApproverRemoved'
                            values={{
                              name: editedFromData.name,
                              email: editedFromData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                      {editedToData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueApproverAdded'
                            values={{
                              name: editedToData.name,
                              email: editedToData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {editedField?.value === EditableIssueFields.ISSUE_WATCHERS && (
                <Box data-testid='watchers-timeline-event'>
                  {parseWatcherUsers(editedTo?.value).map((user) => (
                    <TimelineEventTypography key={`added-${user.email}`}>
                      <Trans
                        t={t}
                        i18nKey='issueTimeline.watcherAdded'
                        values={{
                          name: user.name,
                          email: user.email,
                        }}
                      />
                    </TimelineEventTypography>
                  ))}
                  {parseWatcherUsers(editedFrom?.value).map((user) => (
                    <TimelineEventTypography key={`removed-${user.email}`}>
                      <Trans
                        t={t}
                        i18nKey='issueTimeline.watcherRemoved'
                        values={{
                          name: user.name,
                          email: user.email,
                        }}
                      />
                    </TimelineEventTypography>
                  ))}
                </Box>
              )}

              {editedField?.value === EditableIssueFields.IMPACTED_ENTITY && (
                <TimelineEventTypography data-testid='related-org-unit-timeline-event'>
                  {renderRelatedOrgUnits(editedFrom?.value, editedTo?.value)}
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.EXECUTION_TYPE && (
                <TimelineEventTypography data-testid='execution-type-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={
                      editedFrom?.value?.length
                        ? 'issueTimeline.executionTypeChanged'
                        : 'issueTimeline.executionTypeSet'
                    }
                    values={{
                      from: editedFrom?.value,
                      to: editedTo?.value,
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.ATTACHMENTS && (
                <IssueTimelineAttachmentsEvents
                  attachments={
                    event.bodyData.parameters.attachments?.value ?? []
                  }
                />
              )}

              {editedField?.value === EditableIssueFields.TITLE && (
                <TimelineEventTypography data-testid='title-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey='issueTimeline.titleChanged'
                    values={{
                      from: editedFrom?.value,
                      to: editedTo?.value,
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.DESCRIPTION && (
                <TimelineEventTypography data-testid='description-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={
                      editedFrom?.value?.length
                        ? 'issueTimeline.descriptionChanged'
                        : 'issueTimeline.descriptionSet'
                    }
                    values={{
                      from: editedFrom?.value,
                      to: editedTo?.value,
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.IMPACT &&
                impactTimelineEventKey && (
                  <TimelineEventTypography data-testid='impact-timeline-event'>
                    <Trans
                      t={t}
                      i18nKey={impactTimelineEventKey}
                      values={{
                        from: truncateText(editedFrom?.value),
                        to: truncateText(editedTo?.value),
                      }}
                    />
                  </TimelineEventTypography>
                )}

              {editedField?.value === EditableIssueFields.CLASSIFICATION && (
                <TimelineEventTypography data-testid='classification-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={
                      editedFrom?.value?.length
                        ? 'issueTimeline.classificationChanged'
                        : 'issueTimeline.classificationSet'
                    }
                    values={{
                      from: getLabel(editedFrom?.value, classificationLabelMap),
                      to: getLabel(editedTo?.value, classificationLabelMap),
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.ROOT_CAUSE && (
                <TimelineEventTypography data-testid='root-cause-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={getTextFieldTimelineKey(
                      'rootCause',
                      !!editedFrom?.value?.length,
                      !!editedTo?.value?.length,
                    )}
                    values={{
                      from: truncateText(editedFrom?.value),
                      to: truncateText(editedTo?.value),
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value ===
                EditableIssueFields.MANAGEMENT_RESPONSE && (
                <TimelineEventTypography data-testid='management-response-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={getTextFieldTimelineKey(
                      'managementResponse',
                      !!editedFrom?.value?.length,
                      !!editedTo?.value?.length,
                    )}
                    values={{
                      from: truncateText(editedFrom?.value),
                      to: truncateText(editedTo?.value),
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.RESOLUTION && (
                <TimelineEventTypography data-testid='resolution-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={
                      editedFrom?.value?.length
                        ? 'issueTimeline.resolutionChanged'
                        : 'issueTimeline.resolutionSet'
                    }
                    values={{
                      from: getLabel(editedFrom?.value, resolutionLabelMap),
                      to: getLabel(editedTo?.value, resolutionLabelMap),
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.RECOMMENDATIONS && (
                <TimelineEventTypography data-testid='recommendations-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={getTextFieldTimelineKey(
                      'recommendations',
                      !!editedFrom?.value?.length,
                      !!editedTo?.value?.length,
                    )}
                    values={{
                      from: truncateText(editedFrom?.value),
                      to: truncateText(editedTo?.value),
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value ===
                EditableIssueFields.INCLUDE_IN_AUDIT_REPORT && (
                <TimelineEventTypography data-testid='include-in-audit-report-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={
                      editedTo?.value === 'true'
                        ? 'issueTimeline.includeInAuditReportIncluded'
                        : 'issueTimeline.includeInAuditReportExcluded'
                    }
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.DATE_PUBLISHED && (
                <TimelineEventTypography data-testid='date-published-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={getTextFieldTimelineKey(
                      'datePublished',
                      !!editedFrom?.value?.length,
                      !!editedTo?.value?.length,
                    )}
                    values={{
                      from: editedFrom?.value,
                      to: editedTo?.value,
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.REMEDIATION && (
                <TimelineEventTypography data-testid='remediation-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={getTextFieldTimelineKey(
                      'remediation',
                      !!editedFrom?.value?.length,
                      !!editedTo?.value?.length,
                    )}
                    values={{
                      from: truncateText(editedFrom?.value),
                      to: truncateText(editedTo?.value),
                    }}
                  />
                </TimelineEventTypography>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
